import type { Block, Change, ChildNode, Document } from '@gitbook/slate';
import type { List } from '@gitbook/util-immutable';

export type ConflictKeyOrigin = 'head' | 'base';

// Conflict data for blocks that have a conflict
export type ConflictNodeData = {
    origin: ConflictKeyOrigin;
    type: 'modified' | 'removed';
};

/**
 * Annotate a block to indicate it originates in a conflict.
 */
export function annotateBlockWithConflict(block: Block, conflict: ConflictNodeData): Block {
    return (
        block
            .merge({
                key: getConflictKey(block.key, conflict.origin),
                data: block.data.set('conflict', conflict),
            })
            // Update all keys of descendants to avoid duplicate keys with other annotated block
            // The schemaToStripConflicts will rollback the keys when needed
            .mapDescendants((descendant: ChildNode) =>
                // `set('key', …)` only rewrites the immutable `key` field and returns the
                // same node type. TS can't unify the `set` signature across the `Block |
                // Inline | Text` union, so we call it through `Block` (any member works).
                (descendant as Block).set('key', getConflictKey(descendant.key, conflict.origin))
            )
    );
}

/**
 * Test if a document has conflicts with pending resolution
 */
export function hasDocumentConflicts(document: Document): boolean {
    return getDocumentConflicts(document).size > 0;
}

/**
 * Test if a block has a conflict.
 */
export function hasBlockConflict(block: Block): boolean {
    return isConflictKey(block.key) && hasConflictData(block);
}

/**
 * Test if a block has a conflict from a specific side.
 */
export function hasBlockConflictFrom(block: Block, origin: ConflictKeyOrigin): boolean {
    return isConflictKeyFrom(block.key, origin) && hasBlockConflict(block);
}

/**
 * List all nodes with conflict.
 */
export function getDocumentConflicts(document: Document): List<Block> {
    return document.nodes.filter(
        (block) => hasBlockConflict(block) && isConflictKeyFrom(block.key, 'head')
    );
}

/**
 * Test if a node key was generated for a conflict
 */
export function isConflictKey(key: string): boolean {
    return key.endsWith('_head') || key.endsWith('_base');
}

/**
 * Test if a node key was generated for a conflict and is coming from a specific origin.
 */
export function isConflictKeyFrom(key: string, origin: ConflictKeyOrigin): boolean {
    return isConflictKey(key) && getConflictKeyOrigin(key) === origin;
}

/**
 * Get key for the conflict.
 * Since conflicts occur between nodes having the same identifier
 * we need to duplicate these blocks with different keys.
 */
export function getConflictKey(key: string, origin: ConflictKeyOrigin): string {
    return `${key}_${origin}`;
}

/**
 * Get original key before conflict.
 * Key for conflicts are in the format: "{key}_head" or "{key}_base"
 */
export function getOriginalKey(
    key: string,
    // If true, the root key without conflict suffix will be returned.
    // Previously because of a bug with tables, we were appending multiple conflict suffixes.
    root = true
): string {
    const originalKey = key.replace(/_(head|base)$/, '');
    if (!root) {
        return originalKey;
    }
    if (originalKey === key) {
        return originalKey;
    }
    return getOriginalKey(originalKey, root);
}

/**
 * Return the origin of a conflict key.
 */
export function getConflictKeyOrigin(key: string): ConflictKeyOrigin {
    return key.endsWith('_base') ? 'base' : 'head';
}
/**
 * Return the counterpart origin of a conflict key.
 */
export function getConflictKeyCounterpartOrigin(key: string): ConflictKeyOrigin {
    return getConflictKeyOrigin(key) === 'head' ? 'base' : 'head';
}

/**
 * Return the counterpart key of a conflict block key
 */
export function getConflictCounterpartKey(key: string, root = true): string {
    const counterpartOrigin = getConflictKeyCounterpartOrigin(key);
    const originalKey = getOriginalKey(key, root);
    return getConflictKey(originalKey, counterpartOrigin);
}

/**
 * Test if a node has conflict data.
 */
export function hasConflictData(node: Block): boolean {
    return node.data?.has('conflict');
}

/**
 * Pick a conflicting block for resolution.
 * It removes the other conflict and clean the conflict in this node.
 */
export function pickConflict(change: Change, blockKey: string): Change {
    const block = change.value.document.assertBlock(blockKey);

    if (!hasBlockConflict(block)) {
        return change;
    }

    const counterpartKey = getConflictCounterpartKey(block.key);

    change.removeNodeByKey(counterpartKey);

    return change;
}
